"use strict";
/**
 * Sport Mode UI
 * Sport-agnostic order-button layout resolution and slippage-curve math,
 * shared by the baseball and basketball button layouts.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveOrderLayout = resolveOrderLayout;
exports.isSportLayoutActive = isSportLayoutActive;
exports.isCombinedPricingMode = isCombinedPricingMode;
exports.applyOrderLayout = applyOrderLayout;
exports.updateOrderLayoutVisibility = updateOrderLayoutVisibility;
exports.evaluateSlippageCurve = evaluateSlippageCurve;
const side_label_1 = require("./side-label");
/**
 * Resolve which order-button layout should be visible.
 *
 * `forceDefaultButtons` is a pure display override: it wins over any active
 * sport mode without clearing the sport flags, so switching it off restores
 * the sport layout. The sport flags are mutually exclusive at their write
 * sites; the check order only makes the tiebreak deterministic.
 */
function resolveOrderLayout(settings) {
    if (settings.forceDefaultButtons === true)
        return "traditional";
    if (settings.basketballMode === true)
        return "basketball";
    if (settings.soccerMode === true)
        return "soccer";
    if (settings.baseballMode === true)
        return "baseball";
    return "traditional";
}
/** True when a sport-specific button layout (not traditional) is showing. */
function isSportLayoutActive(settings) {
    return resolveOrderLayout(settings) !== "traditional";
}
/**
 * True when per-button manual pricing applies: a sport layout is showing AND
 * manual price mode is on. Under `forceDefaultButtons` this is false, so the
 * traditional buttons fall back to the global manual-price controls.
 */
function isCombinedPricingMode(settings) {
    return isSportLayoutActive(settings) && settings.manualPriceMode === true;
}
/** Container element IDs per layout. */
const LAYOUT_CONTAINER_IDS = {
    traditional: "traditional-order-buttons",
    baseball: "baseball-mode-container",
    basketball: "basketball-mode-container",
    soccer: "soccer-mode-container",
};
/**
 * Show the container for the given layout and hide the others.
 * Null-safe for containers absent from the DOM (e.g. in tests).
 */
function applyOrderLayout(layout) {
    for (const [key, id] of Object.entries(LAYOUT_CONTAINER_IDS)) {
        const el = document.getElementById(id);
        if (el)
            el.classList.toggle("hidden", key !== layout);
    }
    // A hidden column has no width, so labels written while it was hidden could
    // not be fitted. Now that one is visible, fit them.
    (0, side_label_1.refitSideLabels)();
}
/** Apply the layout resolved from the given settings. */
function updateOrderLayoutVisibility(settings) {
    applyOrderLayout(resolveOrderLayout(settings));
}
/**
 * Evaluate a slippage curve at the given factor:
 * `base + linear*factor + quadratic*factor²`, rounded to the nearest whole
 * percent. The quadratic term keeps the ramp smooth at low factors while
 * letting aggressive buttons pull away at high factors.
 */
function evaluateSlippageCurve(curve, factor) {
    return Math.round(curve.base + curve.linear * factor + curve.quadratic * factor * factor);
}
